/**
 * Helper para disparar Google Ads conversion "WSP Reservas con fechas" (Secondary).
 *
 * Diseño:
 *   - SOLO se dispara cuando el `cta_position` está en la whitelist `WSP_RESERVAS_POSITIONS`
 *     (clicks WSP desde /reservas post-formulario o variantes restrictivas).
 *   - El conversion action en Google Ads quedó `primary_for_goal=False` y el
 *     account goal `CONTACT~WEBSITE` permanece `biddable=False` — por eso este
 *     evento NO altera la optimización del algoritmo, solo da telemetría.
 *   - Dedup con `sessionStorage` por la combinación (arrival, departure, num_adults,
 *     cta_position, apartment_id). `transaction_id` determinístico por contexto.
 *   - Defensivo: jamás lanza, jamás bloquea. Si `gtag` aún no cargó (gtag.js es
 *     `lazyOnload` en `app/layout.tsx`), el stub bufferea hasta que carga real.
 *
 * Conversion creada vía `scripts/google_ads_create_wsp_reservas_conversion.py` 2026-05-18.
 * ID: 7615661439 · send_to: AW-17712408101/D3MwCP-Kt68cEKXM9_1B
 */

import { fireOpenAiQualifiedLead } from './openai-ads';

export type WspReservasCtaPosition =
  | 'reservas_recommended_group'
  | 'reservas_group_quote'
  | 'reservas_no_results'
  | 'reservas_min_stay_exception'
  | 'reservas_checkinout_exception'
  | 'reservas_sticky_mobile'
  | 'reservas_on_request_card'
  | 'reservas_min_stay_card'
  | 'reservas_checkinout_card'
  | 'reservas_results_banner'
  | 'intake_header'
  | 'intake_footer'
  | 'intake_contact'
  | 'intake_apartment_detail'
  | 'intake_hero'
  | 'intake_mid'
  | 'intake_final'
  | 'intake_sticky_mobile'
  | 'intake_apartments_hub_final_cta'
  | 'header_qualified_bypass'
  | 'footer_qualified_bypass';

const WSP_RESERVAS_POSITIONS: ReadonlySet<string> = new Set<WspReservasCtaPosition>([
  'reservas_recommended_group',
  'reservas_group_quote',
  'reservas_no_results',
  'reservas_min_stay_exception',
  'reservas_checkinout_exception',
  'reservas_sticky_mobile',
  'reservas_on_request_card',
  'reservas_min_stay_card',
  'reservas_checkinout_card',
  // Ampliación 2026-06 — handoff wsp-calificado W4: positions del asistente y
  // franja de resultados. Solo entran aquí flujos que SIEMPRE llevan fechas
  // (el sheet solo dispara la conversión en handleContinue, nunca en el escape).
  'reservas_results_banner',
  'intake_header',
  'intake_footer',
  'intake_contact',
  'intake_apartment_detail',
  'intake_hero',
  'intake_mid',
  'intake_final',
  'intake_sticky_mobile',
  'intake_apartments_hub_final_cta',
  // Bypass calificado del CTA global (header/footer) con búsqueda activa en la
  // URL — siempre lleva fechas, mismo criterio W4 que el resto del set.
  'header_qualified_bypass',
  'footer_qualified_bypass',
]);

// Label viene de env var; fallback al label creado y verificado 2026-05-18.
// Override por env permite rotar sin redeploy.
const SEND_TO =
  process.env.NEXT_PUBLIC_GADS_WSP_RESERVAS_CONVERSION_LABEL ||
  'AW-17712408101/D3MwCP-Kt68cEKXM9_1B';

export interface WspReservasConversionProps {
  cta_position: string;
  arrival?: string;
  departure?: string;
  num_adults?: number;
  apartment_id?: string;
}

/**
 * Dispara Google Ads conversion SOLO si cta_position está en la whitelist /reservas.
 * No-op si el position no aplica (ej. header, footer, building hero, sticky global, landing WhatsApp).
 *
 * @returns `true` si disparó, `false` si fue no-op o dedup hit.
 */
export function fireWspReservasConversion(props: WspReservasConversionProps): boolean {
  // Guard 1: SSR
  if (typeof window === 'undefined') return false;

  // Guard 2: whitelist de positions — única vía para que esto se dispare
  if (!WSP_RESERVAS_POSITIONS.has(props.cta_position)) return false;

  // Guard 2.5 — handoff W4: la conversión SOLO califica con fechas + huéspedes.
  if (!props.arrival || !props.departure) return false;
  if (!props.num_adults || props.num_adults < 1) return false;

  fireOpenAiQualifiedLead({ kind: 'apartment', date: props.arrival,
    departure: props.departure, guests: props.num_adults, product: props.apartment_id });

  // Guard 3: gtag stub disponible (definido en app/layout.tsx aún si gtag.js no cargó)
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== 'function') return false;

  try {
    // Dedup key estable por combinación de contexto
    const dedupKey = [
      'gads_wsp_reservas',
      props.arrival || '',
      props.departure || '',
      props.num_adults ?? '',
      props.cta_position,
      props.apartment_id || '',
    ].join(':');

    const already = window.sessionStorage.getItem(dedupKey);
    if (already) return false;

    // transaction_id determinístico — si el cliente refresca y vuelve a clickear
    // el mismo CTA con misma combinación, Google Ads dedupe server-side por counting=ONE_PER_CLICK
    const transactionId = [
      'wsp-reservas',
      props.cta_position,
      props.arrival || 'none',
      props.departure || 'none',
      props.num_adults ?? 'none',
      props.apartment_id || 'none',
    ].join('-');

    gtag('event', 'conversion', {
      send_to: SEND_TO,
      value: 0,
      currency: 'COP',
      transaction_id: transactionId,
    });

    window.sessionStorage.setItem(dedupKey, '1');

    if (process.env.NODE_ENV !== 'production') {
      // Log solo en dev para no ruidar producción
      console.log('[gads:wsp-reservas] conversion fired', {
        cta_position: props.cta_position,
        transaction_id: transactionId,
      });
    }

    return true;
  } catch (err) {
    // No bloquear UX por error de tracking
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[gads:wsp-reservas] error firing conversion', err);
    }
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// "WSP Tours calificado" (Secondary) — Etapa 3 plan wsp-calificado 2026-06-10.
// Conversion creada vía scripts/google_ads_create_wsp_tours_conversion.py
// (repo 14_seo-pacartagena). ID: 7643663938 · primary_for_goal=False.
// ─────────────────────────────────────────────────────────────────────────────

const TOURS_SEND_TO =
  process.env.NEXT_PUBLIC_GADS_WSP_TOURS_CONVERSION_LABEL ||
  'AW-17712408101/IXQvCMKc5LwcEKXM9_1B';

const WSP_TOURS_POSITIONS: ReadonlySet<string> = new Set([
  'tour_booking_box',
  'tour_mobile_sticky',
  'intake_header',
  'intake_footer',
  'header_qualified_bypass',
  'footer_qualified_bypass',
]);

/** Conversión Secondary "WSP Tours calificado" — solo con fecha + personas. */
export function fireWspToursConversion(props: {
  cta_position: string;
  tour_slug?: string;
  tour_date?: string;
  guests?: number;
}): boolean {
  if (typeof window === 'undefined') return false;
  if (!TOURS_SEND_TO) return false;
  if (!WSP_TOURS_POSITIONS.has(props.cta_position)) return false;
  if (!props.tour_date || !props.guests || props.guests < 1) return false;

  fireOpenAiQualifiedLead({ kind: 'tour', date: props.tour_date,
    guests: props.guests, product: props.tour_slug });

  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== 'function') return false;

  try {
    const dedupKey = ['gads_wsp_tours', props.tour_slug || '', props.tour_date, props.guests, props.cta_position].join(':');
    if (window.sessionStorage.getItem(dedupKey)) return false;

    gtag('event', 'conversion', {
      send_to: TOURS_SEND_TO,
      value: 0,
      currency: 'COP',
      transaction_id: ['wsp-tours', props.tour_slug || 'none', props.tour_date, props.guests].join('-'),
    });
    window.sessionStorage.setItem(dedupKey, '1');

    if (process.env.NODE_ENV !== 'production') {
      console.log('[gads:wsp-tours] conversion fired', {
        cta_position: props.cta_position,
        tour_slug: props.tour_slug,
      });
    }

    return true;
  } catch {
    return false;
  }
}
